import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { http, API_BASE } from "@/lib/api";
import { readLocation } from "@/components/LocationPill";
import {
  MapPin, Sun, CloudSun, CloudFog, CloudRain, Snowflake, CloudLightning,
  Wind, Drop, Warning, Question, ArrowRight, ThermometerSimple,
} from "@phosphor-icons/react";

const abs = (u) => (!u ? "" : u.startsWith("http") ? u : `${API_BASE}${u}`);

const WEATHER_CODES = [
  [[0], "Clear sky", Sun],
  [[1, 2, 3], "Partly cloudy", CloudSun],
  [[45, 48], "Foggy", CloudFog],
  [[51, 53, 55, 56, 57, 61, 63, 65, 66, 67, 80, 81, 82], "Rain", CloudRain],
  [[71, 73, 75, 77, 85, 86], "Snow", Snowflake],
  [[95, 96, 99], "Thunderstorm", CloudLightning],
];
const weatherFor = (code) => WEATHER_CODES.find(([codes]) => codes.includes(code)) || [null, "Changing skies", CloudSun];

const SEV_STYLE = {
  severe: "border-rose-300 bg-rose-50 text-rose-900",
  warning: "border-amber-300 bg-amber-50 text-amber-900",
  advisory: "border-sky-300 bg-sky-50 text-sky-900",
};

export default function NearMeFeed() {
  const [loc, setLoc] = useState(readLocation);
  const [sightings, setSightings] = useState(null);
  const [alerts, setAlerts] = useState(null);
  const [weather, setWeather] = useState(null);

  useEffect(() => {
    const onChange = () => setLoc(readLocation());
    window.addEventListener("np:location-changed", onChange);
    return () => window.removeEventListener("np:location-changed", onChange);
  }, []);

  useEffect(() => {
    if (!loc?.lat || !loc?.lon) return;
    let alive = true;
    const { lat, lon } = loc;

    http.get("/nature/observations/nearby", { params: { lat, lon, radius_km: 150, limit: 6 } })
      .then(({ data }) => { if (alive) setSightings(data.observations || []); })
      .catch(() => { if (alive) setSightings([]); });

    http.get("/weather/alerts/local", { params: { lat, lon, radius_km: 250, limit: 4 } })
      .then(({ data }) => { if (alive) setAlerts(data.alerts || []); })
      .catch(() => { if (alive) setAlerts([]); });

    fetch(`https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current=temperature_2m,relative_humidity_2m,weather_code,wind_speed_10m&timezone=auto`)
      .then((r) => r.json())
      .then((d) => { if (alive) setWeather(d?.current || null); })
      .catch(() => { if (alive) setWeather(null); });

    return () => { alive = false; };
  }, [loc?.lat, loc?.lon]);

  const place = [loc?.city, loc?.state].filter(Boolean).join(", ") || loc?.country || "your area";
  const [wLabel, WIcon] = weather ? [weatherFor(weather.weather_code)[1], weatherFor(weather.weather_code)[2]] : [null, CloudSun];

  return (
    <section className="py-14 md:py-20 px-5 md:px-10 bg-[color:var(--np-bg,#F5EBDC)]" data-testid="near-me-section">
      <div className="max-w-[1200px] mx-auto">
        <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)] mb-2">
          / Your patch of the planet
        </div>
        <div className="flex flex-wrap items-end justify-between gap-3">
          <h2 className="font-serif-display text-3xl md:text-5xl leading-[1.02] tracking-tight text-[#1B4332]">
            Near you{place ? ` — ${place}` : ""}.
          </h2>
          <div className="inline-flex items-center gap-1.5 font-mono-tech text-[10px] tracking-[0.22em] uppercase text-[color:var(--np-muted,#3A3830)]" data-testid="near-me-location">
            <MapPin size={12} weight="fill" className="text-[color:var(--np-brand,#B85F2A)]" />
            {loc?.display_name || place}
          </div>
        </div>

        <div className="mt-8 grid gap-4 md:grid-cols-[0.9fr_1.1fr_1.4fr]">
          {/* Weather now */}
          <div className="rounded-2xl bg-[#1B4332] text-[#F5EBDC] p-5 flex flex-col" data-testid="near-me-weather">
            <div className="font-mono-tech text-[9px] tracking-[0.28em] uppercase text-[#E4A15C]">Right now outside</div>
            {weather ? (
              <>
                <div className="mt-4 flex items-center gap-3">
                  <WIcon size={38} weight="fill" className="text-[#E4A15C]" />
                  <div>
                    <div className="font-serif-display text-4xl leading-none">{Math.round(weather.temperature_2m)}°C</div>
                    <div className="mt-1 text-sm text-[#E5D8C4]/85">{wLabel}</div>
                  </div>
                </div>
                <div className="mt-auto pt-5 grid grid-cols-2 gap-2 text-xs text-[#E5D8C4]/85">
                  <div className="inline-flex items-center gap-1.5"><Wind size={13} weight="fill" className="text-[#E4A15C]" /> {Math.round(weather.wind_speed_10m)} km/h</div>
                  <div className="inline-flex items-center gap-1.5"><Drop size={13} weight="fill" className="text-[#E4A15C]" /> {weather.relative_humidity_2m}% humidity</div>
                </div>
              </>
            ) : (
              <div className="mt-4 flex items-center gap-2 text-sm text-[#E5D8C4]/70">
                <ThermometerSimple size={16} weight="fill" /> Weather unavailable right now
              </div>
            )}
          </div>

          {/* Local alerts */}
          <div className="rounded-2xl bg-white border border-black/10 p-5" data-testid="near-me-alerts">
            <div className="font-mono-tech text-[9px] tracking-[0.28em] uppercase text-[color:var(--np-muted,#3A3830)]">Weather & field alerts</div>
            {alerts === null ? (
              <div className="mt-4 text-sm text-[#3A3830]/60">Checking…</div>
            ) : alerts.length === 0 ? (
              <div className="mt-4 flex items-center gap-2 text-sm text-[#3A3830]">
                <Sun size={16} weight="fill" className="text-[color:var(--np-brand,#B85F2A)]" />
                All quiet within 250 km — no active alerts.
              </div>
            ) : (
              <ul className="mt-3 space-y-2">
                {alerts.map((a, i) => (
                  <li key={a.id || i} data-testid={`near-me-alert-${i}`}
                      className={`rounded-lg border px-3 py-2 text-sm ${SEV_STYLE[a.severity] || "border-black/10 bg-black/5 text-[#1B4332]"}`}>
                    <div className="flex items-center gap-1.5 font-medium">
                      <Warning size={13} weight="fill" /> <span className="line-clamp-1">{a.title}</span>
                    </div>
                    <div className="mt-0.5 font-mono-tech text-[9px] tracking-[0.2em] uppercase opacity-70">
                      {a.severity}{a.distance_km != null ? ` · ${a.distance_km} km away` : ""}
                    </div>
                  </li>
                ))}
              </ul>
            )}
            <Link to="/notifications" className="mt-3 inline-flex items-center gap-1 font-mono-tech text-[9px] tracking-[0.24em] uppercase text-[color:var(--np-brand,#B85F2A)] hover:gap-2 transition-all">
              All alerts <ArrowRight size={11} weight="bold" />
            </Link>
          </div>

          {/* Nearby sightings */}
          <div className="rounded-2xl bg-white border border-black/10 p-5" data-testid="near-me-sightings">
            <div className="font-mono-tech text-[9px] tracking-[0.28em] uppercase text-[color:var(--np-muted,#3A3830)]">Latest sightings near you</div>
            {sightings === null ? (
              <div className="mt-4 text-sm text-[#3A3830]/60">Scanning the area…</div>
            ) : sightings.length === 0 ? (
              <div className="mt-4 text-sm text-[#3A3830]">
                Nothing logged within 150 km yet — <a href="#identify" className="text-[color:var(--np-brand,#B85F2A)] underline">be the first</a>.
              </div>
            ) : (
              <ul className="mt-3 grid sm:grid-cols-2 gap-2">
                {sightings.map((o, i) => (
                  <li key={o.id}>
                    <Link
                      to={`/observations/${o.id}`}
                      data-testid={`near-me-obs-${i}`}
                      className="group flex gap-2.5 rounded-lg border border-black/10 hover:border-[color:var(--np-brand,#B85F2A)] p-2 transition-colors"
                    >
                      {o.cover ? (
                        <img src={abs(o.cover)} alt="" loading="lazy" className="w-14 h-14 rounded-md object-cover shrink-0 bg-black/5" />
                      ) : (
                        <div className="w-14 h-14 rounded-md bg-[#F5EBDC] grid place-items-center shrink-0">
                          <MapPin size={16} weight="fill" className="text-[color:var(--np-brand,#B85F2A)]/50" />
                        </div>
                      )}
                      <div className="min-w-0">
                        <div className="text-sm font-medium leading-tight line-clamp-2 text-[#1B4332]">{o.title}</div>
                        <div className="mt-1 flex items-center gap-1.5 font-mono-tech text-[9px] tracking-[0.18em] uppercase text-[color:var(--np-muted,#3A3830)]">
                          <span>{o.distance_km} km</span>
                          {o.location?.city && <span>· {o.location.city}</span>}
                          {o.needs_identification && (
                            <span className="inline-flex items-center gap-0.5 text-emerald-700"><Question size={9} weight="bold" /> ID?</span>
                          )}
                        </div>
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
            <Link to="/observations" className="mt-3 inline-flex items-center gap-1 font-mono-tech text-[9px] tracking-[0.24em] uppercase text-[color:var(--np-brand,#B85F2A)] hover:gap-2 transition-all">
              Explore the map <ArrowRight size={11} weight="bold" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
